/**
 * Reusable "Add to Project" control for any swarm task card.
 * Self-contained: fetches this swarm's projects, lets the user pick one or
 * create a new one inline, then attaches the run to it. Only needs
 * { swarm, runId, taskTitle }. Styling is theme-agnostic (inline styles) so it
 * drops cleanly into every swarm's bespoke task drawer.
 */
import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { FolderPlus, Plus, Check, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AddToProjectButton({ swarm, runId, taskTitle = "", accent = "#2DD4A8", compact = false }) {
  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [busyId, setBusyId] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onEsc); };
  }, [open]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/projects?swarm=${encodeURIComponent(swarm)}`);
      setProjects(data.projects || []);
    } catch (_) { setProjects([]); }
    setLoading(false);
  };

  const toggle = (e) => {
    e.stopPropagation();
    const next = !open;
    setOpen(next);
    if (next) load();
  };

  const attach = async (projectId) => {
    setBusyId(projectId);
    try {
      await axios.post(`${API}/projects/${projectId}/tasks`, { swarm, run_id: runId, task_title: taskTitle });
      toast.success("Added to project");
      setOpen(false);
    } catch (e) {
      toast.error("Could not add to project");
    }
    setBusyId(null);
  };

  const createAndAttach = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/projects`, { swarm, name: newName.trim() });
      await attach(data.id);
      setNewName("");
    } catch (e) {
      toast.error("Could not create project");
    }
    setCreating(false);
  };

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-flex" }} data-testid={`add-to-project-${runId}`}>
      <button
        onClick={toggle}
        title="Add to project"
        data-testid={`add-to-project-btn-${runId}`}
        style={{
          display: "inline-flex", alignItems: "center", gap: 5, cursor: "pointer",
          background: "none", border: "none", color: accent, fontSize: 12, padding: 0,
        }}
      >
        <FolderPlus size={compact ? 13 : 14} />
        {!compact && <span>Project</span>}
      </button>

      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          data-testid={`add-to-project-menu-${runId}`}
          style={{
            position: "absolute", right: 0, top: "calc(100% + 6px)", width: 250, zIndex: 200,
            background: "#0F1830", border: "1px solid #1E2D4A", borderRadius: 12,
            boxShadow: "0 12px 40px rgba(0,0,0,0.45)", padding: 10,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "#8593AC", fontWeight: 600 }}>
              Add to project
            </span>
            <button onClick={() => setOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#8593AC", display: "flex" }}>
              <X size={13} />
            </button>
          </div>

          <div style={{ maxHeight: 180, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
            {loading && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#8593AC", fontSize: 12, padding: "6px 4px" }}>
                <Loader2 size={13} className="animate-spin" /> Loading…
              </div>
            )}
            {!loading && projects.length === 0 && (
              <div style={{ color: "#6B7A94", fontSize: 12, padding: "6px 4px" }}>No projects yet for this swarm.</div>
            )}
            {!loading && projects.map((p) => (
              <button
                key={p.id}
                onClick={() => attach(p.id)}
                data-testid={`add-to-project-option-${p.id}`}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                  background: "rgba(255,255,255,0.03)", border: "1px solid #1E2D4A", borderRadius: 8,
                  padding: "7px 9px", cursor: "pointer", color: "#DCE3F0", fontSize: 13, textAlign: "left",
                }}
              >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</span>
                {busyId === p.id ? <Loader2 size={13} className="animate-spin" style={{ color: accent }} /> : <Check size={13} style={{ color: accent, opacity: 0.5 }} />}
              </button>
            ))}
          </div>

          <div style={{ borderTop: "1px solid #1E2D4A", marginTop: 8, paddingTop: 8, display: "flex", gap: 6 }}>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") createAndAttach(); }}
              placeholder="New project…"
              data-testid={`add-to-project-newname-${runId}`}
              style={{
                flex: 1, background: "#0A1120", border: "1px solid #1E2D4A", borderRadius: 8,
                padding: "6px 8px", fontSize: 13, color: "#fff", outline: "none",
              }}
            />
            <button
              onClick={createAndAttach}
              disabled={creating || !newName.trim()}
              data-testid={`add-to-project-create-${runId}`}
              style={{
                display: "flex", alignItems: "center", justifyContent: "center", width: 32,
                background: accent, border: "none", borderRadius: 8, cursor: "pointer",
                opacity: creating || !newName.trim() ? 0.5 : 1, color: "#04211A",
              }}
            >
              {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
